import {
  CLIENT_SECRET,
  AZURE_AD_CLIENT_ID,
  AZURE_AD_REDIRECT_URI,
  AZURE_AD_TENANT,
  LOCALES,
  APPLICATION,
} from '../env'
import { useAPI, apiClient } from '../api/index'
import { Router } from 'vue-router'
import {
  EP_AUTH_CLIENT_IDENTITY,
  EP_AUTH_CUSTOMER_IDENTITY,
  EP_AUTH_AZURE_AD_VALIDATION_TOKEN,
} from '../api/endpoints/uri'
import * as msal from '@azure/msal-browser'

import { countryVars as _countryVars } from './countryVars'
import { useGlobalStore } from '../../../../customer-care-portal/src/stores/globalStore'
import {
  AuthenticationResult,
  SilentRequest,
  PopupRequest,
  AccountInfo,
} from '@azure/msal-browser'

interface CountryVars {
  LOCALE: string
  BANNER: string
  CURRENCY: string
}

const countryVars = _countryVars as CountryVars[]
const storage = APPLICATION === 'customer-portal' ? sessionStorage : localStorage

export function authenticateClientIdentity(
  clientSecret?: string,
  identityId?: string
) {
  const payload = {
    clientId: 'gv_gls_authorized_api_client',
    clientSecret: clientSecret || CLIENT_SECRET,
    identityId: identityId || '12345820',
    identityEmail: 'ext.Daniel.Cunha@grandvision.com',
    identityLocale: '',
  }

  return useAPI({
    method: 'post',
    url: EP_AUTH_CLIENT_IDENTITY,
    data: JSON.stringify(payload),
  })
}

export function getAccessToken() {
  return storage.getItem('access_token')
}

export function isAccessTokenValid() {
  const currTime = Date.now()
  const expiresAt = parseInt(storage.getItem('expires_at') || '0', 10)
  const diff = (expiresAt - currTime) / 60000 // Gets time difference in minutes

  // If the token is about to expire (less than 10 minutes) then return false to trigger the refresh!
  return storage.getItem('access_token') && diff > 10
}

export function setAccessToken(accessToken: string, expiresAt?: number) {
  storage.setItem('access_token', accessToken)
  if (expiresAt) {
    storage.setItem('expires_at', expiresAt.toString())
  } else {
    // Default to 1 hour if expiresIn is not provided
    const oneHour = 3600 * 1000
    const expirationDate = Date.now() + oneHour
    storage.setItem('expires_at', expirationDate.toString())
  }
}

export function getCustomerIdentityToken(redirectUrl: string) {
  return useAPI({
    method: 'post',
    url: EP_AUTH_CUSTOMER_IDENTITY,
    data: {
      locale: LOCALES.split(', ')[0],
      redirectUrl,
    },
  })
}

export async function userLogout(router?: Router, args?: any) {
  clearStorageItems()

  if (sessionStorage && sessionStorage.length > 0 && APPLICATION === 'customer-care-portal') {
    const homeAccountId = getHomeAccountIdFromSessionStorage()

    const account = msalInstance()
      .getAllAccounts()
      .find((acc) => acc.homeAccountId === homeAccountId) as AccountInfo

    await msalInstance().logoutPopup({
      account: account,
      postLogoutRedirectUri: AZURE_AD_REDIRECT_URI,
      mainWindowRedirectUri: AZURE_AD_REDIRECT_URI,
    })
  } else {
    // Case logout on Customer Portal
    router?.push(args)
  }
}

export async function customerUserLogout(router?: Router, args?: any) {
  clearStorageItems()

  router?.push(args)
}

function clearStorageItems() {
  storage.removeItem('access_token')
  storage.removeItem('expiresIn')
  storage.removeItem('expires_at')
  storage.removeItem('user_groups')
  storage.removeItem('userCache')
  storage.removeItem('userName')
}

export async function refreshToken() {
  const homeAccountId = getHomeAccountIdFromSessionStorage()

  const currentAccount = msalInstance()
    .getAllAccounts()
    .find((acc) => acc.homeAccountId === homeAccountId) as AccountInfo

  const silentRequest: SilentRequest = {
    scopes: [`${AZURE_AD_CLIENT_ID}/.default`],
    account: currentAccount,
    forceRefresh: false,
  }

  const request: PopupRequest = {
    scopes: [`${AZURE_AD_CLIENT_ID}/.default`],
    loginHint: currentAccount?.username, // For v1 endpoints, use upn from idToken claims
  }

  const tokenResponse = await msalInstance()
    .acquireTokenSilent(silentRequest)
    .catch(async (error) => {
      if (error) {
        // fallback to interaction when silent call fails
        return await msalInstance()
          .acquireTokenPopup(request)
          .catch((error) => {
            console.error('error: ', error)
          })
      }
    })

  return await handleTokenResponse(tokenResponse)
}

function getHomeAccountIdFromSessionStorage(): string {
  let homeAccountId = ''

  for (var [_, valueStorage] of Object.entries(sessionStorage)) {
    for (var [key, value] of Object.entries(JSON.parse(valueStorage))) {
      if (key === 'homeAccountId') {
        homeAccountId = `${value}`
        break
      }
    }
    if (homeAccountId.length > 0) break
  }

  return homeAccountId
}

const msalConfig = {
  auth: {
    clientId: AZURE_AD_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${AZURE_AD_TENANT}`,
    redirectUri: AZURE_AD_REDIRECT_URI,
    postLogoutRedirectUri: AZURE_AD_REDIRECT_URI,
  },
  cache: {
    cacheLocation: 'sessionStorage', // This can be "localStorage" or "sessionStorage"
    storeAuthStateInCookie: false, // Set to true if you are having issues on IE11 or Edge
    claimsBasedCachingEnabled: true, // Enable claims-based caching
  },
}

const msalInstance = (() => {
  let instance: msal.PublicClientApplication | null = null

  return () => {
    if (!instance) {
      instance = new msal.PublicClientApplication(msalConfig)
    }

    return instance
  }
})()

// Add here scopes for id token to be used at MS Identity Platform endpoints.
const loginRequest = {
  scopes: [`${AZURE_AD_CLIENT_ID}/.default`],
}

// Initialization function for MSAL
async function initializeMsal() {
  try {
    await msalInstance().initialize()
    console.log('MSAL initialized successfully')
  } catch (err) {
    console.error('MSAL initialization error:', err)
    throw err
  }
}

function authenticateAzureADToken(identityId?: string) {
  const payload = {
    identityToken: identityId,
  }

  return useAPI({
    method: 'post',
    url: EP_AUTH_AZURE_AD_VALIDATION_TOKEN,
    data: JSON.stringify(payload),
  })
}

export async function userAzureADLogin() {
  try {
    // Ensure MSAL is initialized before calling loginRedirect
    await initializeMsal()
    await msalInstance().loginRedirect(loginRequest)
    // The loginRedirect method does not return a promise, so you cannot handle the response here.
    // You need to handle the response in a callback or event handler after the redirect.
  } catch (err) {
    console.error('Login redirect error:', err)
    return false
  }
  return true
}

// Handle the response after the redirect
// This code initializes MSAL and handles the authentication response after a redirect.
// It retrieves the token response, stores the username in local storage, and processes the token.
// NOTE: Do not change this code block. Altering it may cause the authentication flow to break.
initializeMsal()
  .then(() => {
    msalInstance()
      .handleRedirectPromise()
      .then((tokenResponse) => {
        if (tokenResponse && tokenResponse.account) {
          localStorage.setItem('userName', `${tokenResponse.account.username}`)
          handleTokenResponse(tokenResponse)
        }
      })
      .catch((err) => {
        console.error('Handle redirect promise error:', err)
      })
  })
  .catch((err) => {
    console.error('MSAL initialization error:', err)
  })

async function handleTokenResponse(tokenResponse: void | AuthenticationResult) {
  if (tokenResponse) {
    if (tokenResponse.account?.idTokenClaims?.groups) {
      const groups: string[] = tokenResponse.account.idTokenClaims
        .groups as string[]
      localStorage.setItem('user_groups', groups.join(','))
    }

    const accessToken = tokenResponse.accessToken
    if (accessToken && accessToken.length > 0) {
      const { fetch } = authenticateAzureADToken(accessToken)
      await fetch()
      setAccessToken(
        tokenResponse.accessToken,
        tokenResponse.expiresOn?.getTime()
      )
      setLocalStorageConfigs()
    }
    return accessToken
  }
}

function setLocalStorageConfigs() {
  const globalStore = useGlobalStore()
  const locale = localStorage.getItem('locale')
  // ? Checks if the localStorage contains a valid LOCALE variable.
  const foundLocale = countryVars.filter((country) => {
    return country.LOCALE == locale
  })
  // ? If a valid value is found, populate the globalStore with cached values.
  if (foundLocale.length > 0) {
    globalStore.setLocale(foundLocale[0].LOCALE)
    // ? If no valid value found, then populate the globalStore with default values.
  } else {
    globalStore.setLocale(LOCALES.split(', ')[0])
  }
}
